import type { Brief, Campaign, PublicAnalyticsRefreshStatus } from "../../../shared/types";

const SEARCH_CONSOLE = "google.search_console.read";
const REPORT_NAMES: Record<string, string> = {
  "gsc.daily": "Daily search performance",
  "gsc.top_queries": "Top search terms",
  "gsc.top_pages": "Top search pages",
  "gsc.query_to_page": "Search terms by page",
};
const OPEN_BRIEF_STATES = new Set(["ideation", "researching", "briefed", "drafting", "voice_review", "in_review", "approved", "scheduled", "blocked"]);

export function nextPlannedBrief(briefs: Brief[], now = new Date()): Brief | null {
  return briefs.filter((brief) => brief.scheduled_at && OPEN_BRIEF_STATES.has(brief.state)
    && Date.parse(brief.scheduled_at) >= now.getTime())
    .sort((left, right) => Date.parse(left.scheduled_at!) - Date.parse(right.scheduled_at!))[0] ?? null;
}

export function overdueBriefCount(briefs: Brief[], now = new Date()): number {
  return briefs.filter((brief) => brief.scheduled_at && OPEN_BRIEF_STATES.has(brief.state)
    && Date.parse(brief.scheduled_at) < now.getTime()).length;
}

export function campaignForBrief(brief: Brief | null, campaigns: Campaign[]): Campaign | null {
  return brief ? campaigns.find((campaign) => campaign.id === brief.campaign_id) ?? null : null;
}

export type SearchReadiness = {
  kind: "loading" | "unavailable" | "not_connected" | "not_configured" | "failed" | "partial" | "blocked" | "paused" | "queued" | "running" | "on_demand" | "scheduled";
  detail: string;
  lastSuccessAt: string | null;
};

export function searchReadiness(
  refreshes: PublicAnalyticsRefreshStatus[] | undefined,
  state: "loading" | "error" | "ready",
): SearchReadiness {
  if (state === "loading") return { kind: "loading", detail: "Checking Search Console…", lastSuccessAt: null };
  if (state === "error" || !Array.isArray(refreshes)) return { kind: "unavailable", detail: "Search status could not be loaded.", lastSuccessAt: null };
  const sources = refreshes.filter((source) => source.capability === SEARCH_CONSOLE);
  if (!sources.length) return { kind: "not_connected", detail: "No Search Console property is assigned to this brand.", lastSuccessAt: null };
  const lastSuccessAt = sources.length === 1 ? sources[0]!.last_success_at : null;
  const failedReports = sources.flatMap((source) => source.reports.filter((report) =>
    report.last_run ? report.last_run.status === "failed" : report.consecutive_failure_count > 0,
  ).map((report) => report.report_key));
  if (failedReports.length) return {
    kind: "failed",
    detail: `${[...new Set(failedReports)].map((key) => REPORT_NAMES[key] ?? key).join(", ")} report failed. Other reports may still be available.`,
    lastSuccessAt,
  };
  const partialReports = sources.flatMap((source) => source.reports.filter((report) =>
    report.last_run?.status === "partial",
  ).map((report) => report.report_key));
  if (partialReports.length) return {
    kind: "partial",
    detail: `${[...new Set(partialReports)].map((key) => REPORT_NAMES[key] ?? key).join(", ")} report is incomplete. Review its coverage in Data Views.`,
    lastSuccessAt,
  };
  if (sources.some((source) => source.schedule?.last_attempt?.status === "failed")) {
    return { kind: "failed", detail: "The latest scheduled search collection failed. Review the source details.", lastSuccessAt };
  }
  if (sources.some((source) => source.blockers.length || source.state === "blocked" || source.state === "unavailable")) {
    return { kind: "blocked", detail: "Search collection needs attention in Connections.", lastSuccessAt };
  }
  if (sources.some((source) => source.state === "not_configured")) {
    return { kind: "not_configured", detail: "Search Console is connected. Finish collection setup in Connections by saving its Action if needed and choosing a schedule.", lastSuccessAt };
  }
  // A system-disabled report under an enabled source is blocked by the backend, not user-paused.
  if (sources.every((source) => source.state === "paused"
    || (source.enabled === false && source.reports.every((report) => !report.enabled)))) {
    return { kind: "paused", detail: "Automatic search collection is paused.", lastSuccessAt };
  }
  if (sources.some((source) => source.enabled !== true || source.state === "mixed")) {
    return { kind: "blocked", detail: "Search report schedules differ. Review each source in Data Views.", lastSuccessAt };
  }
  if (sources.some((source) => source.state === "queued")) {
    return { kind: "queued", detail: "A search collection check is due; completion has not been confirmed.", lastSuccessAt };
  }
  if (sources.some((source) => source.state === "running")) {
    return { kind: "running", detail: "Search collection is in progress.", lastSuccessAt };
  }
  if (sources.some((source) => source.state === "on_demand")) {
    return { kind: "on_demand", detail: "Search collection is available on request; no schedule is confirmed.", lastSuccessAt };
  }
  return {
    kind: "scheduled",
    detail: sources.length === 1 ? "Search collection is scheduled." : `${sources.length} search properties are assigned.`,
    lastSuccessAt,
  };
}
